import type BillHandler from '@itinerary/BillHandler';
import { type SubmitBuyerPassenger } from '@store/form';
import type ItineraryPackage from '@itinerary/ItineraryPackage';
import { type PaxType } from '@utils/types';

/**
 * invoice entries of invoice
 */
export interface InvoiceEntry {
    /**
     * Type of invoice entry's passenger
     *
     * @type {PaxType}
     * @memberof InvoiceEntry
     */
    paxType: PaxType;

    /**
     * Amount of the invoice
     *
     * @type {number}
     * @memberof InvoiceEntry
     */
    price: number;

    /**
     * Number of passengers of invoice entry
     *
     * @type {number}
     * @memberof InvoiceEntry
     */
    count: number;

    /**
     * title of invoice entry
     *
     * @type {string}
     * @memberof InvoiceEntry
     */
    title?: string;

    /**
     * discount of this entry
     *
     * @type {number}
     * @memberof InvoiceEntry
     */
    discount?: number;

    /**
     * type of discount
     *
     * @type {DiscountType}
     * @memberof InvoiceEntry
     */
    discountType?: DiscountType;

    showAsUnknown?: boolean;
}

/**
 * Indicates the type of bill's discount
 */
export enum DiscountType {
    Special,
    Coupon,
    Score,
    FirstPurchase,
}

/**
 * Information of discount entry of bill
 */
export interface DiscountEntry {
    /**
     * Title of the discount entry
     *
     * @type {string}
     * @memberof DiscountEntry
     */
    title: string;

    /**
     * Amount of discount price of the discount entry
     *
     * @type {number}
     * @memberof DiscountEntry
     */
    price: number;

    /**
     * Type of the discount (special or coupon)
     *
     * @type {DiscountType}
     * @memberof DiscountEntry
     */
    type: DiscountType;
}

/**
 * Indicates the status of coupon
 */
export enum CouponStatus {
    None,
    Checking,
    Accepted,
    Rejected,
}

export enum GiftCardStatus {
    None,
    Checking,
    Accepted,
    Rejected,
}

/**
 * Indicates status of information registration
 */
export enum InformationRegisterStatus {
    None,
    Registering,
    Registered,
    Error,
    RegisteredFromSession,
}

export interface ItineraryPackageInvoiceEntries {
    itineraryPackage?: ItineraryPackage;
    prices: InvoiceEntry[];
}

export enum WorkflowStatus {
    CheckingFinalizability,
    ReservesAreNotFinalizable,
    FinalizingReserves,
    FinalizationCompleted,
    CouponIsNotAcceptable,
    VerifyingPayment,
    TicketsGenerated,
}
export interface FinalizationStatus {
    status: WorkflowStatus;
    title: string;
    message: string;
    paymentWarningDescription: string | null;
    isSuccessful: boolean | null;
    ticketFiles: TicketFile[];
    hasSuggestedServiceReserve: boolean;
    coupon?: { amount: number; discountCode: string };
}

export interface TicketFile {
    url: string;
    previewUrl: string;
}

/**
 * Holds the title of each type of discount
 *
 * @type {[key in DiscountType]: string}
 */
export const discountTitles: { [key in DiscountType]: string } = {
    [DiscountType.Special]: 'تخفیف ویژه',
    [DiscountType.Coupon]: 'کد تخفیف',
    [DiscountType.Score]: 'تخفیف امتیاز کاربری',
    [DiscountType.FirstPurchase]: 'تخفیف کاربر جدید',
};

export type InvoiceSessionData = {
    entries: ItineraryPackageInvoiceEntries[];
    billHandlers: BillHandler[];
    discounts: DiscountEntry[];
    pax: SubmitBuyerPassenger[];
    billInfo: {
        code: string | null;
        id: number | null;
        reserveId: number | null;
    };
    gateway: string;
    contact: {
        mobile: string;
        destinationMobile: string;
        landline: string;
        email: string;
    };
    customData: any;
    useCredit: boolean;
};
